import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PageHeader from '../../components/common/PageHeader';
import { fetchO365Licenses } from '../../features/o365/O365Slice';

const getLicenseName = (license = {}) => license.licenseName || license.name || license.skuPartNumber || license.productName || 'Licence O365';
const toNumber = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getEsetOrganizationName = (license = {}) => license.organizationDetails?.name || license.organizationName || '';
const getEsetDeviceName = (device = {}) => device.name || device.hostname || device.hostName || 'Appareil ESET';
const getEsetDeviceOs = (device = {}) => {
  if (typeof device.OS === 'string') return device.OS;
  return [device.OS?.name, device.OS?.version].filter(Boolean).join(' ') || device.os || '-';
};

function useClientOrganizationNames() {
  const { data: organizations = [] } = useSelector((state) => state.organizations.organizations);
  return useMemo(
    () => organizations.map((organization) => organization.name).filter(Boolean),
    [organizations],
  );
}

function filterByOrganization(items, organizationNames, getName) {
  const hasOrganizationName = items.some((item) => getName(item));
  if (!organizationNames.length || !hasOrganizationName) return items;
  return items.filter((item) => organizationNames.includes(getName(item)));
}

function SummaryCard({ icon, title, value, helper, color = 'primary.main' }) {

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{ color, display: 'flex', mt: 0.5 }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">{title}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{helper}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function ClientO365Licenses() {
  const dispatch = useDispatch();
  const organizationNames = useClientOrganizationNames();
  const { data: o365Licenses = [], loading, error, initialized } = useSelector((state) => state.o365.licenses);
  const { data: esetLicenses = [], loading: esetLoading, error: esetError } = useSelector((state) => state.eset.licenses);
  const { data: esetDevices = [] } = useSelector((state) => state.eset.devices);
  const [expandedLicense, setExpandedLicense] = useState(null);
  const [expandedEsetLicense, setExpandedEsetLicense] = useState(null);

  useEffect(() => {
    if ((!initialized || error) && !loading) dispatch(fetchO365Licenses());
  }, [dispatch, initialized, loading, error]);

  const clientName = organizationNames[0] || 'Client';
  const licenses = useMemo(() => filterByOrganization(
    o365Licenses,
    organizationNames,
    (license) => license.organizationName,
  ).filter((license) => toNumber(license.total) <= 1000), [o365Licenses, organizationNames]);

  const clientEsetLicenses = useMemo(() => filterByOrganization(
    esetLicenses,
    organizationNames,
    getEsetOrganizationName,
  ), [esetLicenses, organizationNames]);

  const summary = useMemo(() => {
    const o365Used = licenses.reduce((sum, license) => sum + toNumber(license.used), 0);
    const o365Total = licenses.reduce((sum, license) => sum + toNumber(license.total), 0);
    const esetUsed = clientEsetLicenses.reduce((sum, license) => sum + toNumber(license.used), 0);
    const esetTotal = clientEsetLicenses.reduce((sum, license) => sum + toNumber(license.quantity), 0);
    return {
      o365: { count: licenses.length, used: o365Used, total: o365Total },
      eset: { count: clientEsetLicenses.length, used: esetUsed, total: esetTotal },
    };
  }, [licenses, clientEsetLicenses]);

  const columns = useMemo(() => [
    {
      accessorFn: getLicenseName,
      id: 'licenseName',
      header: 'Licence',
      size: 260,
      filterVariant: 'autocomplete',
      Cell: ({ renderedCellValue }) => <Typography sx={{ fontWeight: 700 }}>{renderedCellValue}</Typography>,
    },
    {
      accessorFn: (row) => toNumber(row.used),
      id: 'used',
      header: 'Utilisées',
      size: 120,
    },
    {
      accessorFn: (row) => toNumber(row.total),
      id: 'total',
      header: 'Total',
      size: 120,
    },
    {
      accessorFn: (row) => Math.max(toNumber(row.total) - toNumber(row.used), 0),
      id: 'available',
      header: 'Disponibles',
      size: 140,
      Cell: ({ cell }) => {
        const value = toNumber(cell.getValue());
        return <Chip size="small" color={value > 0 ? 'success' : 'warning'} label={value} />;
      },
    },
    {
      accessorFn: (row) => {
        const total = toNumber(row.total);
        if (!total) return 0;
        return Math.min((toNumber(row.used) / total) * 100, 100);
      },
      id: 'usage',
      header: 'Occupation',
      size: 220,
      Cell: ({ row }) => {
        const used = toNumber(row.original.used);
        const total = toNumber(row.original.total);
        const percent = total ? Math.min((used / total) * 100, 100) : 0;
        return (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 180 }}>
            <LinearProgress
              variant="determinate"
              value={percent}
              color={percent >= 100 ? 'error' : percent >= 85 ? 'warning' : 'primary'}
              sx={{ height: 8, borderRadius: 5, flex: 1 }}
            />
            <Typography variant="caption" color="text.secondary">{Math.round(percent)}%</Typography>
          </Stack>
        );
      },
    },
    {
      accessorFn: (row) => formatDate(row.nextCycleDateTime),
      id: 'nextCycleDateTime',
      header: 'Renouvellement',
      size: 180,
    },
  ], []);

  const renderUsers = (license) => {
      const users = Array.isArray(license.matchedUsers) ? license.matchedUsers : [];
      if (!users.length) return null;
      return (
        <Box sx={{ p: { xs: 1.5, md: 2.5 }, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 800 }}>
            Utilisateurs associés ({users.length})
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, borderRadius: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Nom</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Adresse e-mail</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Statut</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user, index) => {
                  const name = user.displayName || user.userPrincipalName || user.mail || 'Utilisateur';
                  const email = user.userPrincipalName || user.mail || '-';
                  const deleted = user.deleted || user.accountEnabled === false;
                  return (
                    <TableRow key={`${email}-${index}`} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>
                      <TableCell>{email}</TableCell>
                      <TableCell>
                        <Chip size="small" label={deleted ? 'Inactif' : 'Actif'} color={deleted ? 'default' : 'success'} variant="outlined" />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      );
    };

  const getEsetDevicesForLicense = (license) => {
    if (Array.isArray(license.relatedDevices)) return license.relatedDevices;

    const tenantPublicId = String(license.tenantPublicId || '');
    const tenantName = String(license.tenantName || '').trim().toLowerCase();
    return esetDevices.filter((device) => (
      (tenantPublicId && String(device.tenantPublicId || '') === tenantPublicId)
      || (tenantName && String(device.tenantName || '').trim().toLowerCase() === tenantName)
    ));
  };

  const renderEsetDevices = (license) => {
    const devices = getEsetDevicesForLicense(license);
    return (
      <Box sx={{ p: { xs: 1.5, md: 2.5 }, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 800 }}>
          Appareils associés ({devices.length})
        </Typography>
        {!devices.length ? (
          <Typography variant="body2" color="text.secondary">Aucun appareil actif associé à cette licence.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360, borderRadius: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Appareil</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Système</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>IP locale</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>IP publique</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Dernière synchronisation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.map((device, index) => (
                  <TableRow key={device._id || device.id || `${getEsetDeviceName(device)}-${index}`} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{getEsetDeviceName(device)}</TableCell>
                    <TableCell>{getEsetDeviceOs(device)}</TableCell>
                    <TableCell>{device.ipLAN || '-'}</TableCell>
                    <TableCell>{device.ipWAN || '-'}</TableCell>
                    <TableCell>{formatDate(device.lastSync) || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <PageHeader section={clientName} title="Vos licences" sx={{ my: 1, mb: 3 }} />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, mb: 3 }}>
        <SummaryCard
          icon={<MailOutlineIcon />}
          title="Licences O365"
          value={loading ? '...' : summary.o365.count}
          helper={`${summary.o365.used} utilisées / ${summary.o365.total} prévues`}
          color="info.main"
        />
        <SummaryCard
          icon={<SecurityIcon />}
          title="Licences ESET"
          value={esetLoading ? '...' : `${summary.eset.used} / ${summary.eset.total}`}
          helper={`${summary.eset.count} licence(s) - appareils associés dans le détail`}
          color="success.main"
        />
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>Erreur de chargement des licences O365.</Alert> : null}
      {esetError ? <Alert severity="error" sx={{ mb: 2 }}>Erreur de chargement des licences ESET.</Alert> : null}

      <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5 }}>Détail des licences O365</Typography>
      {loading ? (
        <Skeleton variant="rectangular" width="100%" height={520} />
      ) : licenses.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="h6" color="text.secondary">Aucune licence O365 trouvée</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table stickyHeader>
            <TableHead><TableRow>
              <TableCell sx={{ fontWeight: 800, width: 48 }} />
              <TableCell sx={{ fontWeight: 800 }}>Licence</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Utilisées</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Total</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Disponibles</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Occupation</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Renouvellement</TableCell>
            </TableRow></TableHead>
            <TableBody>{licenses.map((license, index) => {
              const key = license.id || license._id || license.skuId || `${getLicenseName(license)}-${index}`;
              const used = toNumber(license.used);
              const total = toNumber(license.total);
              const percent = total ? Math.min((used / total) * 100, 100) : 0;
              const expanded = expandedLicense === key;
              return <Fragment key={key}>
                <TableRow key={key} hover>
                  <TableCell><IconButton size="small" onClick={() => setExpandedLicense(expanded ? null : key)}>{expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton></TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{getLicenseName(license)}</TableCell>
                  <TableCell>{used}</TableCell><TableCell>{total}</TableCell>
                  <TableCell><Chip size="small" label={Math.max(total - used, 0)} color={total > used ? 'success' : 'warning'} /></TableCell>
                  <TableCell><Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 160 }}><LinearProgress variant="determinate" value={percent} sx={{ flex: 1, height: 8, borderRadius: 5 }} /><Typography variant="caption">{Math.round(percent)}%</Typography></Stack></TableCell>
                  <TableCell>{formatDate(license.nextCycleDateTime) || '-'}</TableCell>
                </TableRow>
                {expanded && <TableRow key={`${key}-detail`}><TableCell colSpan={7} sx={{ p: 0 }}>{renderUsers(license)}</TableCell></TableRow>}
              </Fragment>;
            })}</TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="h6" sx={{ fontWeight: 900, mt: 4, mb: 1.5 }}>Détail des licences ESET</Typography>
      {esetLoading ? (
        <Skeleton variant="rectangular" width="100%" height={360} />
      ) : clientEsetLicenses.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="h6" color="text.secondary">Aucune licence ESET trouvée</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table stickyHeader>
            <TableHead><TableRow>
              <TableCell sx={{ fontWeight: 800, width: 48 }} />
              <TableCell sx={{ fontWeight: 800 }}>Licence</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Utilisées</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Total</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Disponibles</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Occupation</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Tenant</TableCell>
            </TableRow></TableHead>
            <TableBody>{clientEsetLicenses.map((license, index) => {
              const key = license.id || license._id || license.licenseKey || `${getLicenseName(license)}-${index}`;
              const used = toNumber(license.used);
              const total = toNumber(license.quantity);
              const percent = total ? Math.min((used / total) * 100, 100) : 0;
              const expanded = expandedEsetLicense === key;
              return <Fragment key={key}>
                <TableRow hover>
                  <TableCell><IconButton size="small" onClick={() => setExpandedEsetLicense(expanded ? null : key)}>{expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton></TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{getLicenseName(license)}</TableCell>
                  <TableCell>{used}</TableCell><TableCell>{total}</TableCell>
                  <TableCell><Chip size="small" label={Math.max(total - used, 0)} color={total > used ? 'success' : 'warning'} /></TableCell>
                  <TableCell><Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 160 }}><LinearProgress variant="determinate" value={percent} color={percent >= 100 ? 'error' : percent >= 85 ? 'warning' : 'success'} sx={{ flex: 1, height: 8, borderRadius: 5 }} /><Typography variant="caption">{Math.round(percent)}%</Typography></Stack></TableCell>
                  <TableCell>{license.tenantName || '-'}</TableCell>
                </TableRow>
                {expanded && <TableRow><TableCell colSpan={7} sx={{ p: 0 }}>{renderEsetDevices(license)}</TableCell></TableRow>}
              </Fragment>;
            })}</TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
